"use client";

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { logger } from "@/lib/logger";

const TOUR_STORAGE_KEY = "jobwright_product_tour_completed";
const TOUR_TASK_KEY = "product-tour-completed";

interface TourContextType {
  isTourOpen: boolean;
  hasCompletedTour: boolean;
  isLoading: boolean;
  startTour: () => void;
  completeTour: () => Promise<void>;
  skipTour: () => Promise<void>;
  closeTour: () => void;
  resetTour: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

interface TourProviderProps {
  children: ReactNode;
  autoStart?: boolean;
}

/**
 * TourProvider Component
 *
 * Provides tour state and actions throughout the app
 */
export function TourProvider({ children, autoStart = false }: TourProviderProps) {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Check tour completion status on mount
  useEffect(() => {
    const checkTourStatus = async () => {
      try {
        // First check localStorage for immediate response
        const localStatus = localStorage.getItem(TOUR_STORAGE_KEY);
        const isLocallyCompleted = localStatus === "true";

        // Then check server for persistent status
        const response = await fetch("/api/onboarding/tour");
        if (response.ok) {
          const data = await response.json();
          const isServerCompleted = data.hasCompletedTour || false;

          setHasCompletedTour(isServerCompleted);

          // Sync localStorage with server
          if (isServerCompleted) {
            localStorage.setItem(TOUR_STORAGE_KEY, "true");
          }

          // Auto-start tour if not completed and autoStart is enabled
          if (!isServerCompleted && autoStart) {
            setIsTourOpen(true);
          }
        } else {
          // Fallback to localStorage if API fails
          setHasCompletedTour(isLocallyCompleted);

          if (!isLocallyCompleted && autoStart) {
            setIsTourOpen(true);
          }
        }
      } catch (error) {
        logger.error("Error checking tour status:", error instanceof Error ? { message: error.message, stack: error.stack } : { error });
        // Fallback to localStorage
        const localStatus = localStorage.getItem(TOUR_STORAGE_KEY);
        const isLocallyCompleted = localStatus === "true";
        setHasCompletedTour(isLocallyCompleted);

        if (!isLocallyCompleted && autoStart) {
          setIsTourOpen(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkTourStatus();
  }, [autoStart]);

  // Start the tour
  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  // Complete the tour
  const completeTour = useCallback(async () => {
    setIsTourOpen(false);
    setHasCompletedTour(true);

    // Update localStorage immediately
    localStorage.setItem(TOUR_STORAGE_KEY, "true");

    // Update server
    try {
      const response = await fetch("/api/onboarding/tour", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          taskKey: TOUR_TASK_KEY,
          tourId: "product-tour",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save tour completion");
      }

      logger.info("Tour completion saved successfully");
    } catch (error) {
      logger.error("Error saving tour completion:", error instanceof Error ? { message: error.message, stack: error.stack } : { error });
      // Don't revert local state on error - user still completed the tour
    }
  }, []);

  // Skip the tour
  const skipTour = useCallback(async () => {
    setIsTourOpen(false);
    setHasCompletedTour(true);

    // Update localStorage immediately
    localStorage.setItem(TOUR_STORAGE_KEY, "true");

    // Update server with skip flag
    try {
      const response = await fetch("/api/onboarding/tour", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          taskKey: TOUR_TASK_KEY,
          tourId: "product-tour",
          skipped: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save tour skip");
      }

      logger.info("Tour skip saved successfully");
    } catch (error) {
      logger.error("Error saving tour skip:", error instanceof Error ? { message: error.message, stack: error.stack } : { error });
      // Don't revert local state on error
    }
  }, []);

  // Close the tour without saving completion
  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  // Reset the tour (for testing or re-showing)
  const resetTour = useCallback(() => {
    setHasCompletedTour(false);
    setIsTourOpen(true);
    localStorage.removeItem(TOUR_STORAGE_KEY);

    // Optionally delete from server
    fetch("/api/onboarding/tour", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        taskKey: TOUR_TASK_KEY,
      }),
    }).catch((error) => {
      logger.error("Error resetting tour on server:", error instanceof Error ? { message: error.message, stack: error.stack } : { error });
    });
  }, []);

  const value: TourContextType = {
    isTourOpen,
    hasCompletedTour,
    isLoading,
    startTour,
    completeTour,
    skipTour,
    closeTour,
    resetTour,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

/**
 * useTourContext Hook
 *
 * Access tour state and actions from any component
 */
export function useTourContext() {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error("useTourContext must be used within a TourProvider");
  }
  return context;
}
